import { useState } from 'react';
import { CalendarOff, Plus, Trash2 } from 'lucide-react';
import { Badge, Button, Input } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from './ClosuresPanel.module.css';
import { useCallClosures } from '../hooks/useCallClosures';
import { useCanManageCalls } from '../hooks/useCanManageCalls';
import type { ClosureOverride } from '../api/callsApi';

// Closure dates are calendar days stored as UTC midnight — format them in UTC
// so the viewer's own timezone can't shift the day shown.
function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function todayKey(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function closureState(c: ClosureOverride): 'active' | 'upcoming' | 'past' {
  const today = todayKey();
  const start = c.startDate.slice(0, 10);
  const end = c.endDate.slice(0, 10);
  if (end < today) return 'past';
  if (start > today) return 'upcoming';
  return 'active';
}

// Practice-wide closures (holidays, office events). While one covers today,
// every agent at this location opens with the closure message instead of its
// usual opening line — injected per call by the inbound webhook.
export function ClosuresPanel() {
  const { closures, isLoading, isError, create, remove } = useCallClosures();
  const canManage = useCanManageCalls();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [message, setMessage] = useState('');
  const [callbackPromise, setCallbackPromise] = useState('');

  const visible = closures.filter((c) => closureState(c) !== 'past');
  const canSubmit = !!startDate && !!endDate && endDate >= startDate && !!message.trim();

  const handleCreate = () => {
    if (!canSubmit) return;
    create.mutate(
      { startDate, endDate, message: message.trim(), callbackPromise: callbackPromise.trim() },
      {
        onSuccess: () => {
          setStartDate('');
          setEndDate('');
          setMessage('');
          setCallbackPromise('');
        },
      },
    );
  };

  return (
    <div className={styles.panel}>
      <div>
        <div className={patterns.kicker}>
          <CalendarOff size={12} /> Office closures
        </div>
        <p className={patterns.panelSubtitle}>
          Closed for a holiday or event? Every agent at this location opens with your closure
          message for those days, then goes back to normal automatically.
        </p>
      </div>

      {isError && (
        <p className={patterns.cellMuted}>Couldn't load closures — refresh to try again.</p>
      )}
      {!isLoading &&
        visible.map((c) => {
          const state = closureState(c);
          return (
            <div key={c.id} className={styles.row}>
              <div>
                <div className={styles.rowDates}>
                  {formatDay(c.startDate)}
                  {c.endDate.slice(0, 10) !== c.startDate.slice(0, 10) &&
                    ` – ${formatDay(c.endDate)}`}{' '}
                  {state === 'active' ? (
                    <Badge variant="warning" size="sm">
                      In effect now
                    </Badge>
                  ) : (
                    <Badge variant="neutral" size="sm">
                      Upcoming
                    </Badge>
                  )}
                </div>
                <div className={styles.rowMessage}>"{c.message}"</div>
                {c.callbackPromise && (
                  <div className={styles.rowMessage}>Callback promise: {c.callbackPromise}</div>
                )}
              </div>
              {canManage && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Trash2 size={13} />}
                  loading={remove.isPending && remove.variables === c.id}
                  onClick={() => remove.mutate(c.id)}
                >
                  Remove
                </Button>
              )}
            </div>
          );
        })}

      {canManage && (
        <>
          <div className={styles.formGrid}>
            <Input
              label="First closed day"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
            />
            <Input
              label="Last closed day"
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
              error={
                endDate && startDate && endDate < startDate
                  ? 'Must be on or after the first day'
                  : undefined
              }
              fullWidth
            />
            <div className={styles.formFull}>
              <Input
                label="What the agent says first while you're closed"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Thanks for calling — our office is closed for the holiday through Monday…"
                maxLength={1000}
                fullWidth
              />
            </div>
            <div className={styles.formFull}>
              <Input
                label="Callback promise during the closure (optional)"
                value={callbackPromise}
                onChange={(e) => setCallbackPromise(e.target.value)}
                placeholder="on Tuesday morning when we reopen"
                maxLength={300}
                fullWidth
              />
            </div>
          </div>
          <div>
            <Button
              size="sm"
              leftIcon={<Plus size={13} />}
              disabled={!canSubmit}
              loading={create.isPending}
              onClick={handleCreate}
            >
              Schedule closure
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
